/*
 * <cart-reco-card> — a single product-recommendation card in the cart drawer.
 * ------------------------------------------------------------------------
 * Self-initializing custom element: its event listeners (variant <select>s and
 * the Add-to-cart button) are wired in connectedCallback, so they are
 * automatically re-bound every time the cart drawer re-renders (cart updates
 * replace the drawer HTML, which re-inserts fresh <cart-reco-card> elements).
 *
 * Variant resolution mirrors the cross-sell popup: only options with more than
 * one value get a <select>; single-value options are fixed (seeded from the
 * first variant) so the full option tuple can still resolve to a variant id.
 */
class CartRecoCard extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const dataEl = this.querySelector('.cart-reco-card__variants');
    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.variants = [];
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    this.idInput = this.querySelector('.cart-reco-card__variant-id');
    this.priceEl = this.querySelector('.cart-reco-card__price');
    this.addBtn = this.querySelector('.cart-reco-card__add');
    this.selects = Array.from(this.querySelectorAll('select[data-reco-option]'));

    // Seed from the first variant so fixed (single-value, no-select) options are set.
    const first = this.variants[0];
    this.selected = Array.isArray(first.options) ? first.options.slice() : [];

    // Options that render a <select> start unselected (must be chosen explicitly).
    this.selects.forEach((select) => {
      const pos = parseInt(select.dataset.optionPosition, 10);
      this.selected[pos] = null;
      select.addEventListener('change', (event) => {
        // The reco slider lives inside <cart-drawer-items>, which has a delegated
        // `change` listener (CartItems.onChange) that would otherwise treat this as a
        // line-item quantity change. Keep the event from bubbling into it.
        event.stopPropagation();
        this.selected[pos] = select.value || null;
        this.updateVariant();
      });
    });

    if (this.addBtn) this.addBtn.addEventListener('click', () => this.add());

    this.updateVariant();
  }

  updateVariant() {
    // Single-variant card: id is already set server-side.
    if (this.selects.length === 0) return;

    const complete = this.selected.every((v) => v != null && v !== '');
    const match = complete
      ? this.variants.find(
          (v) =>
            Array.isArray(v.options) &&
            v.options.length === this.selected.length &&
            v.options.every((o, i) => o === this.selected[i])
        )
      : null;

    // The button stays visually enabled (per the design); add() nudges the user to
    // pick an option if the selection is incomplete.
    if (match) {
      this.idInput.value = match.id;
      if (this.priceEl && match.price) this.priceEl.textContent = match.price;
    } else {
      this.idInput.value = '';
    }
  }

  async add() {
    if (this.addBtn.classList.contains('loading')) return;

    const id = this.idInput && this.idInput.value;
    if (!id) {
      // Incomplete selection — focus the first unchosen option instead of adding.
      const empty = this.selects.find((s) => !s.value);
      empty?.focus();
      return;
    }

    this.addBtn.classList.add('loading');
    this.addBtn.disabled = true;

    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(id), quantity: 1 }),
      });
      const item = await res.json();
      if (!res.ok) {
        this.addBtn.classList.remove('loading');
        this.addBtn.disabled = false;
        return;
      }
      const title =
        item.product_title || this.querySelector('.cart-reco-card__title')?.textContent.trim();

      // Refresh the whole drawer (line items, header count, recommendations) + the
      // cart bubble, then surface the green "added" notice. drawer.refresh() replaces
      // #CartDrawer's HTML, which re-inserts (and re-initializes) the reco cards.
      if (drawer && typeof drawer.refresh === 'function') {
        await drawer.refresh();
        if (typeof drawer.showAddedNotice === 'function') drawer.showAddedNotice(title);
      }
    } catch (e) {
      this.addBtn.classList.remove('loading');
      this.addBtn.disabled = false;
    }
  }
}

if (!customElements.get('cart-reco-card')) {
  customElements.define('cart-reco-card', CartRecoCard);
}

/*
 * <cart-reco-carousel> — desktop navigation arrows for the reco slider.
 * ------------------------------------------------------------------------
 * The track is a CSS scroll-snap flex row. On mobile it is scrolled by touch,
 * which leaves desktop with no affordance at all — the scrollbar is hidden, and
 * a trackpad's horizontal gesture is not discoverable. These arrows step the
 * track one card at a time, so every stop lands on a scroll-snap boundary.
 *
 * Self-initializing like <cart-reco-card>, so the arrows re-bind every time the
 * drawer re-renders. The arrow row stays hidden unless the track actually
 * overflows, and each button disables itself at its end of the track.
 */
class CartRecoCarousel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.track = this.querySelector('[data-reco-track]');
    this.arrows = this.querySelector('[data-reco-arrows]');
    if (!this.track || !this.arrows) return;

    this.buttons = Array.from(this.querySelectorAll('[data-reco-arrow]'));
    this.pendingScroll = null;
    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onManualScroll = this.onManualScroll.bind(this);
    this.sync = this.sync.bind(this);

    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('wheel', this.onManualScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onManualScroll, { passive: true });

    // Drives the arrow state on viewport resize, and on the first layout in
    // which the track has real dimensions.
    this.resizeObserver = new ResizeObserver(this.sync);
    this.resizeObserver.observe(this.track);

    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('wheel', this.onManualScroll);
    this.track?.removeEventListener('pointerdown', this.onManualScroll);
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  onClick(event) {
    const button = event.target.closest('[data-reco-arrow]');
    if (!button || button.disabled) return;

    // The slider lives inside <cart-drawer-items>, and the theme binds delegated
    // click handlers further up. Scrolling the track is not a cart interaction —
    // keep it from being read as one.
    event.stopPropagation();

    this.navigate(button.dataset.recoArrow);
  }

  // A drag or a trackpad swipe abandons whatever step was in flight.
  onManualScroll() {
    this.pendingScroll = null;
  }

  onScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.sync();
    });
  }

  // One card plus the flex gap. Falls back to a full page if the track is empty.
  getStep() {
    const card = this.track.querySelector('.cart-reco-card');
    if (!card) return this.track.clientWidth;

    const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    return card.offsetWidth + gap;
  }

  getMaxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  navigate(direction) {
    const step = this.getStep();
    const delta = direction === 'backward' ? -step : step;

    // scrollTo({ behavior: 'smooth' }) is async, so scrollLeft still reads the
    // old position while a step is animating. Stepping from the live value would
    // make a quick second click recompute the same target and go nowhere; step
    // from the pending target instead, and fall back to the live position once
    // the scroll has settled (see sync).
    const from = this.pendingScroll === null ? this.track.scrollLeft : this.pendingScroll;
    const target = Math.min(Math.max(0, from + delta), this.getMaxScroll());

    this.pendingScroll = target;
    this.track.scrollTo({ left: target, behavior: 'smooth' });
  }

  sync() {
    const maxScroll = this.getMaxScroll();

    // Everything already fits — show no controls rather than dead ones.
    if (maxScroll <= 1) {
      this.arrows.hidden = true;
      return;
    }

    this.arrows.hidden = false;

    // 2px of slack: scroll-snap can leave scrollLeft a fraction short of the
    // extremes, and a button that never disables reads as broken.
    const position = this.track.scrollLeft;

    // The in-flight step has landed — later clicks step from the live position.
    if (this.pendingScroll !== null && Math.abs(position - this.pendingScroll) <= 2) {
      this.pendingScroll = null;
    }

    this.buttons.forEach((button) => {
      button.disabled =
        button.dataset.recoArrow === 'backward' ? position <= 2 : position >= maxScroll - 2;
    });
  }
}

if (!customElements.get('cart-reco-carousel')) {
  customElements.define('cart-reco-carousel', CartRecoCarousel);
}
